import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

type ToastAction = { label: string; onClick: () => void };
interface Toast {
  id: number;
  message: string;
  action?: ToastAction;
}

const ToastCtx = createContext<(message: string, action?: ToastAction) => void>(() => {});

/** Show a transient toast, optionally with an action button (e.g. Undo). */
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));

  const show = useCallback((message: string, action?: ToastAction) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  }, []);

  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="pointer-events-none fixed bottom-5 left-1/2 z-50 flex w-full max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col items-center gap-2 px-4 sm:w-auto sm:px-0">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto flex max-w-full items-center gap-3 rounded-lg bg-neutral-900 px-4 py-2 text-sm text-white shadow-lg"
          >
            <span className="min-w-0 break-words">{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.onClick();
                  dismiss(t.id);
                }}
                className="font-semibold text-blue-300 hover:text-blue-200"
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              title="Dismiss"
              aria-label="Dismiss notification"
              className="text-neutral-400 hover:text-white"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
